import {
  isShowFormArtifact,
  parseClientSideFormToolResultContent,
  pendingFormOf,
  type ShowFormArtifact,
} from '@kanbu/schema/contracts';
import { ChatRole, ToolType } from '@kanbu/schema/enums';

import type { ChatMessageItem } from '../../types';

export interface ActiveClientSideForm {
  toolName: string;
  toolCallId: string;
  artifact: ShowFormArtifact;
}

export type FormToolCallState = 'pending' | 'submitted' | 'canceled';

type AnyToolCall = NonNullable<ChatMessageItem['toolCalls']>[number];

/**
 * Lifecycle of a form tool call. `pending` comes exclusively from the
 * explicit `status` field written when the agent pauses for the form —
 * it is never inferred from missing content, so a settled form can not
 * regress to pending.
 */
export function getFormToolCallState(
  toolCall: AnyToolCall,
): FormToolCallState | null {
  if (
    toolCall.type !== ToolType.Tool ||
    !isShowFormArtifact(toolCall.artifact)
  ) {
    return null;
  }

  if (toolCall.status === 'pending') {
    return 'pending';
  }

  const parsed = parseClientSideFormToolResultContent(toolCall.content);

  if (parsed === 'canceled') {
    return 'canceled';
  }

  if (parsed !== null) {
    return 'submitted';
  }

  /**
   * Completed without submission data — the user dismissed the form,
   * e.g. by sending a chat message while it was pending.
   */
  if (toolCall.status === 'completed') {
    return 'canceled';
  }

  return null;
}

export function getFormSubmissionData(
  toolCall: AnyToolCall,
): Record<string, unknown> | null {
  const parsed = parseClientSideFormToolResultContent(toolCall.content);

  if (parsed === null || parsed === 'canceled') {
    return null;
  }

  return parsed;
}

export function isFormToolCall(
  toolCall: AnyToolCall,
): toolCall is AnyToolCall & {
  type: ToolType.Tool;
  artifact: ShowFormArtifact;
} {
  return (
    toolCall.type === ToolType.Tool && isShowFormArtifact(toolCall.artifact)
  );
}

/**
 * The form the conversation is currently waiting on, read through
 * `blocksOf` — `toolCalls` is a v1-only column that assistant turns no
 * longer write, so reading it here made every pending form vanish on
 * reload.
 */
export function getPendingFormToolCall(
  messages: ChatMessageItem[],
): ActiveClientSideForm | null {
  for (
    let messageIndex = messages.length - 1;
    messageIndex >= 0;
    messageIndex -= 1
  ) {
    const message = messages[messageIndex];

    if (message.role !== ChatRole.Assistant) {
      continue;
    }

    const pending = pendingFormOf(message);

    if (pending && isShowFormArtifact(pending.artifact)) {
      return {
        toolName: pending.name,
        toolCallId: pending.toolCallId,
        artifact: pending.artifact,
      };
    }
  }

  return null;
}

export function findActiveClientSideForm(
  messages: ChatMessageItem[],
): ActiveClientSideForm | null {
  return getPendingFormToolCall(messages);
}

export function isComposerBlockedByClientSideForm(
  form: ActiveClientSideForm | null,
): boolean {
  if (!form) {
    return false;
  }

  if (form.artifact.presentation === 'overlay') {
    return true;
  }

  return form.artifact.disableDismiss;
}

export function shouldHideTranscriptForClientSideForm(
  form: ActiveClientSideForm | null,
): boolean {
  return form?.artifact.presentation === 'overlay';
}
